import React, { useState, useRef, useEffect, useCallback } from 'react';
import ClariMeLogo, { EmbraceMark } from './ClariMeLogo';
import { navigateTo } from '../hooks/useHashRouter';
import { hasExistingAccount, recordAccountHistory } from '../hooks/useAuth';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { hasCompletedOnboarding } from './PreVisitOnboarding';
import {
  checkBiometricAvailability,
  enrollBiometric,
  authenticateWithBiometric,
  isBiometricEnrolled,
  getBiometryLabel,
  removeBiometricCredentials,
  updateBiometricToken,
} from '../services/biometricService';
import { isTokenExpired } from '../services/tokenService';
import {
  signInWithApple,
  signInWithGoogle,
  isAppleSignInAvailable,
  isGoogleSignInAvailable,
  initSocialLogin,
} from '../services/socialLoginService';
import {
  socialAppleSignIn,
  socialGoogleSignIn,
  loginWithPassword,
  setPassword as apiSetPassword,
} from '../services/apiService';

// ── App Review Demo Mode ────────────────────────────────────────────
// Apple reviewers can't receive real email OTP, so these bypass auth
// and sign in with a pre-built demo experience.
const DEMO_PHONE = '5550000001';
const DEMO_EMAIL = 'demo@clarime.health';
const DEMO_CODE = '123456';
const DEMO_USER = {
  phone: '5550000001',
  email: 'demo@clarime.health',
  firstName: 'Demo',
  isDemoMode: true,
};

// API base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// ── iOS keyboard viewport fix ───────────────────────────────────────
// When the keyboard opens in WKWebView, iOS shrinks the visual viewport
// and scrolls the page up—pushing content behind the Dynamic Island.
// This hook resets that scroll offset on every viewport resize event.
function useIOSKeyboardFix() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      // Reset any scroll that WKWebView introduced
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleResize);
    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
    };
  }, []);
}

// ── Icons ───────────────────────────────────────────────────────────

const MicIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 10v2a7 7 0 01-14 0v-2M12 19v4m-4 0h8" />
  </svg>
);

const DocIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ShareIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ArrowLeftIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m0 0l7 7m-7-7l7-7" />
  </svg>
);

const ShieldCheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FaceIdIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M3 8V5a2 2 0 012-2h3" strokeWidth={2} strokeLinecap="round" />
    <path d="M16 3h3a2 2 0 012 2v3" strokeWidth={2} strokeLinecap="round" />
    <path d="M21 16v3a2 2 0 01-2 2h-3" strokeWidth={2} strokeLinecap="round" />
    <path d="M8 21H5a2 2 0 01-2-2v-3" strokeWidth={2} strokeLinecap="round" />
    <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
    <path d="M9.5 15c.83.67 2.17 1 3 1s2.17-.33 3-1" strokeWidth={1.5} strokeLinecap="round" />
  </svg>
);

const ArrowRightIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

// ── Social login icons ─────────────────────────────────────────────

const AppleLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const GoogleLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const LockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

// ── Phone number formatting ─────────────────────────────────────────

function formatPhoneInput(value) {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function getRawDigits(formatted) {
  return formatted.replace(/\D/g, '');
}


// ═════════════════════════════════════════════════════════════════════
// Animated Journey — Icons above path, labels below
// ═════════════════════════════════════════════════════════════════════

// Fresh icons — intentionally different from the old Mic/Doc/Share set
// 1. Waveform (audio capture, not a microphone)
const WaveformIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round">
    <path d="M4 12h2" /><path d="M8 8v8" /><path d="M12 5v14" /><path d="M16 8v8" /><path d="M20 10v4" />
  </svg>
);

// 2. Sparkle-doc (clarity / distillation, not a plain document)
const SparkleDocIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
    <path d="M14 2v6h6" />
    <path d="M12 18v-6" /><path d="M9 15l3-3 3 3" />
  </svg>
);

// 3. Paper-plane / send (sharing with family)
const SendIcon = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22l-4-9-9-4z" />
  </svg>
);

const JOURNEY_STEPS = [
  { Icon: WaveformIcon, label: 'Record', desc: 'Capture your appointment', color: '#0d9488', glow: 'rgba(20,184,166,0.15)' },
  { Icon: SparkleDocIcon, label: 'Clarify', desc: 'Get a clear summary', color: '#0891b2', glow: 'rgba(6,182,212,0.15)' },
  { Icon: SendIcon, label: 'Share', desc: 'Send to loved ones', color: '#7c3aed', glow: 'rgba(139,92,246,0.15)' },
];

const STOP_POSITIONS = [16.66, 50, 83.33];

function JourneyAnimation() {
  const [activeStep, setActiveStep] = useState(-1);

  useEffect(() => {
    let cancelled = false;

    function runCycle() {
      if (cancelled) return;

      const timeline = [
        { delay: 1000, fn: () => setActiveStep(0) },
        { delay: 2600, fn: () => setActiveStep(1) },
        { delay: 2600, fn: () => setActiveStep(2) },
        { delay: 3200, fn: () => {} },               // hold all lit
        { delay: 1200, fn: () => setActiveStep(-1) }, // fade out
      ];

      let elapsed = 0;
      timeline.forEach(({ delay, fn }) => {
        setTimeout(() => { if (!cancelled) fn(); }, elapsed);
        elapsed += delay;
      });

      setTimeout(() => { if (!cancelled) runCycle(); }, elapsed + 600);
    }

    runCycle();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="relative" style={{ paddingTop: 8, paddingBottom: 8 }}>

      {/* ── Icons above the path ── */}
      <div className="flex justify-between px-2 mb-4">
        {JOURNEY_STEPS.map((step, i) => {
          const isActive = activeStep >= i;
          const isCurrent = activeStep === i;
          return (
            <div key={step.label} className="flex justify-center" style={{ width: '33.33%' }}>
              <div
                className="flex items-center justify-center rounded-2xl"
                style={{
                  width: 48,
                  height: 48,
                  background: isActive ? `${step.color}12` : '#f1f5f9',
                  boxShadow: isCurrent ? `0 4px 24px ${step.glow}` : 'none',
                  transform: isCurrent ? 'scale(1.12) translateY(-2px)' : 'scale(1)',
                  transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <step.Icon color={isActive ? step.color : '#94a3b8'} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── The path ── */}
      <div className="relative mx-auto" style={{ height: 6, marginLeft: '16.66%', marginRight: '16.66%' }}>
        {/* Track */}
        <div className="absolute inset-0 rounded-full" style={{ background: '#e2e8f0' }} />
        {/* Animated fill */}
        <div
          className="absolute top-0 left-0 h-full rounded-full"
          style={{
            width: activeStep < 0 ? '0%'
              : activeStep === 0 ? '8%'
              : activeStep === 1 ? '54%'
              : '100%',
            background: 'linear-gradient(90deg, #0d9488, #0891b2, #7c3aed)',
            transition: 'width 700ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        {/* Milestone dots */}
        {JOURNEY_STEPS.map((step, i) => {
          const isActive = activeStep >= i;
          return (
            <div
              key={step.label}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
              style={{ left: i === 0 ? '0%' : i === 1 ? '50%' : '100%' }}
            >
              <div
                className="rounded-full"
                style={{
                  width: isActive ? 12 : 8,
                  height: isActive ? 12 : 8,
                  background: isActive ? step.color : '#cbd5e1',
                  border: isActive ? '2px solid white' : '2px solid #e2e8f0',
                  boxShadow: isActive ? `0 0 10px ${step.glow}` : 'none',
                  transition: 'all 400ms ease',
                }}
              />
            </div>
          );
        })}
      </div>

      {/* ── Labels below the path ── */}
      <div className="flex justify-between px-2 mt-4">
        {JOURNEY_STEPS.map((step, i) => {
          const isActive = activeStep >= i;
          return (
            <div key={step.label} className="flex flex-col items-center" style={{ width: '33.33%' }}>
              <p
                className="font-semibold"
                style={{
                  fontSize: 13,
                  color: isActive ? step.color : '#94a3b8',
                  transition: 'color 400ms ease',
                }}
              >
                {step.label}
              </p>
              <p
                style={{
                  fontSize: 11,
                  lineHeight: '1.35',
                  color: isActive ? '#64748b' : 'transparent',
                  transition: 'color 500ms ease',
                  marginTop: 2,
                  textAlign: 'center',
                }}
              >
                {step.desc}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP 0: Welcome / Intro Screen
// ═════════════════════════════════════════════════════════════════════

function WelcomeIntroStep({ onGetStarted, onSignIn }) {
  return (
    <div className="h-full flex flex-col animate-fade-in overflow-hidden relative">
      {/* Warm layered background */}
      <div className="absolute inset-0" style={{
        background: `
          radial-gradient(ellipse 80% 50% at 20% 80%, rgba(204,251,241,0.7) 0%, transparent 70%),
          radial-gradient(ellipse 60% 40% at 80% 20%, rgba(207,250,254,0.6) 0%, transparent 70%),
          radial-gradient(ellipse 50% 50% at 50% 50%, rgba(245,243,255,0.45) 0%, transparent 70%),
          linear-gradient(170deg, #fefefe 0%, #f5fffe 30%, #fef9f4 60%, #fffefb 100%)
        `,
      }} />
      {/* Subtle grain texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Content — relative to sit above backgrounds */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-6">
        {/* Glow behind logo */}
        <div className="relative mb-6">
          <div className="absolute -inset-5 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(20,184,166,0.12) 0%, transparent 70%)',
          }} />
          <EmbraceMark className="w-24 h-24 relative" id="welcome-mark" />
        </div>

        <h1
          className="text-4xl tracking-[-0.02em] mb-2"
          style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }}
        >
          <span className="font-normal text-teal-600">clari</span>
          <span className="font-semibold text-slate-800">me</span>
        </h1>
        <p className="text-slate-400 text-[15px] mb-1">Your doctor visit, clarified.</p>
        <p className="text-slate-300 text-xs">For you and the people who care about you.</p>
      </div>

      {/* Animated journey */}
      <div className="relative px-6 pb-3">
        <JourneyAnimation />
      </div>

      {/* CTA area */}
      <div className="relative px-6 pb-10 space-y-4">
        <button
          onClick={onGetStarted}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/25 active:scale-[0.98] transition-all"
        >
          Get Started
        </button>

        <p className="text-center text-sm text-slate-400">
          Already have an account?{' '}
          <button
            onClick={onSignIn}
            className="text-teal-600 font-medium active:text-teal-700 transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP: Biometric Auto-Login (returning users with Face ID enrolled)
// ═════════════════════════════════════════════════════════════════════

function BiometricLoginStep({ onSuccess, onFallback, biometryLabel }) {
  const [status, setStatus] = useState('prompting'); // prompting | failed
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    async function attempt() {
      const result = await authenticateWithBiometric();
      if (result.success && result.token && isTokenExpired(result.token)) {
        // Keychain JWT is past its server-side expiry — signing in with it
        // would 401 on every API call. Keep the enrollment (completeSignIn
        // rewrites the Keychain after email re-auth) and fall back to email
        // with a session-expired notice.
        sessionStorage.setItem('clarime_session_expired', '1');
        onFallback();
      } else if (result.success && result.token) {
        onSuccess(result.email || result.phone, result.firstName, result.token);
      } else if (result.success && !result.token) {
        // Legacy enrollment (pre-fix) has no JWT stored — clear stale credentials
        // and force re-auth via email. completeSignIn will then re-enroll with a
        // proper token.
        console.log('[Biometric] Legacy enrollment without token, clearing and falling back to email');
        await removeBiometricCredentials();
        onFallback();
      } else {
        setStatus('failed');
      }
    }
    // Small delay so the screen renders first
    const timer = setTimeout(attempt, 400);
    return () => clearTimeout(timer);
  }, [onSuccess]);

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 animate-fade-in">
      <EmbraceMark className="w-16 h-16 mb-6" id="bio-mark" />

      {status === 'prompting' ? (
        <>
          <FaceIdIcon className="w-14 h-14 text-teal-500 mb-4 animate-pulse" />
          <p className="text-slate-500 text-center">Verifying with {biometryLabel}...</p>
        </>
      ) : (
        <>
          <p className="text-slate-700 font-semibold text-lg mb-2">
            {biometryLabel} didn't work
          </p>
          <p className="text-slate-400 text-sm text-center mb-8">
            You can try again or sign in with your phone number.
          </p>
          <div className="w-full max-w-xs space-y-3">
            <button
              onClick={() => {
                hasAttempted.current = false;
                setStatus('prompting');
              }}
              className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-2xl active:scale-[0.98] transition-all"
            >
              Try {biometryLabel} Again
            </button>
            <button
              onClick={onFallback}
              className="w-full py-3 text-slate-400 text-sm font-medium active:text-slate-600 transition-colors"
            >
              Use email instead
            </button>
          </div>
        </>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP 1: Email Entry
// ═════════════════════════════════════════════════════════════════════

function EmailStep({ onSubmit, loading, isReturning, error: submitError, notice, initialEmail, onAppleSignIn, onGoogleSignIn, onPasswordLogin, hasPassword }) {
  const [email, setEmail] = useState(initialEmail || '');

  // Invite context can load after first render (it's fetched from the API) —
  // adopt the invited email once, but never clobber what the user has typed.
  useEffect(() => {
    if (initialEmail && !email) setEmail(initialEmail);
  }, [initialEmail]); // eslint-disable-line react-hooks/exhaustive-deps
  const [consent, setConsent] = useState(false);
  const inputRef = useRef(null);

  // Don't auto-focus — on iOS it pops the keyboard and hides Apple/Google sign-in buttons below
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  // Returning users already consented during signup
  const canSubmit = isValid && (isReturning || consent);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canSubmit) onSubmit(email.toLowerCase().trim());
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          {isReturning ? 'Welcome back' : 'Create your account'}
        </h1>
        <p className="text-slate-500 leading-relaxed">
          {isReturning
            ? <>Enter your email to sign in.<br />We'll send you a quick code.</>
            : <>Enter your email to get started.<br />We'll send you a code to verify.</>
          }
        </p>
      </div>

      {notice && (
        <div className="mb-5 px-4 py-3 rounded-2xl bg-teal-50 border border-teal-100 text-sm text-teal-700 text-center leading-relaxed">
          {notice}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
            Email address
          </label>
          <input
            ref={inputRef}
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-5 py-4 text-lg rounded-2xl border-2 border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-300"
          />
        </div>

        {submitError && (
          <p className="text-sm text-red-500 text-center">{submitError}</p>
        )}

        {/* Email consent — required for new users */}
        {!isReturning && (
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer group" onClick={() => setConsent(!consent)}>
              <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                consent
                  ? 'bg-teal-500 border-teal-500'
                  : 'border-slate-300 bg-white group-active:border-teal-300'
              }`}>
                {consent && (
                  <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="2.5 7 5.5 10 11.5 4" />
                  </svg>
                )}
              </div>
              <span className="text-[13px] leading-relaxed text-slate-500">
                I agree to receive verification emails and Care Group notifications from ClariMe Health. You can unsubscribe anytime.
              </span>
            </label>
            <p className="text-[11px] text-slate-400 leading-relaxed pl-8">
              View our{' '}
              <a href="https://clarime.health/terms" target="_blank" rel="noopener noreferrer" className="text-teal-500 underline">Terms of Service</a>
              {' '}and{' '}
              <a href="https://clarime.health/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-500 underline">Privacy Policy</a>.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Sending code...
            </span>
          ) : (
            'Send Verification Code'
          )}
        </button>
      </form>

      {/* Social login divider + buttons */}
      {(isAppleSignInAvailable() || isGoogleSignInAvailable()) && (
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">or continue with</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="flex gap-3">
            {isAppleSignInAvailable() && (
              <button
                type="button"
                onClick={onAppleSignIn}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-black text-white font-medium rounded-2xl active:scale-[0.98] transition-all disabled:opacity-40"
              >
                <AppleLogo className="w-5 h-5" />
                <span className="text-[15px]">Apple</span>
              </button>
            )}
            {isGoogleSignInAvailable() && (
              <button
                type="button"
                onClick={onGoogleSignIn}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white border-2 border-slate-200 text-slate-700 font-medium rounded-2xl active:scale-[0.98] transition-all disabled:opacity-40"
              >
                <GoogleLogo className="w-5 h-5" />
                <span className="text-[15px]">Google</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Password login link for returning users */}
      {isReturning && hasPassword && (
        <div className="text-center mt-4">
          <button
            onClick={onPasswordLogin}
            className="text-sm text-teal-600 font-medium active:text-teal-700 transition-colors inline-flex items-center gap-1.5"
          >
            <LockIcon className="w-3.5 h-3.5" />
            Sign in with password
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 justify-center mt-6 text-sm text-slate-400">
        <ShieldCheckIcon className="w-4 h-4" />
        <span>Your information is private and secure</span>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP: Password Login (returning users)
// ═════════════════════════════════════════════════════════════════════

function PasswordStep({ email: initialEmail, onSubmit, onBack, loading, error }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => passwordRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email && password) onSubmit(email.toLowerCase().trim(), password);
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Welcome back</h1>
        <p className="text-slate-500 leading-relaxed">Sign in with your password.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="pw-email" className="block text-sm font-medium text-slate-700 mb-2">Email address</label>
          <input
            id="pw-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-5 py-4 text-lg rounded-2xl border-2 border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-300"
          />
        </div>

        <div className="relative">
          <label htmlFor="pw-password" className="block text-sm font-medium text-slate-700 mb-2">Password</label>
          <input
            ref={passwordRef}
            id="pw-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter your password"
            className="w-full px-5 py-4 pr-12 text-lg rounded-2xl border-2 border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-300"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-[42px] text-slate-400 active:text-slate-600"
          >
            {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-500 text-center">{error}</p>
        )}

        <button
          type="submit"
          disabled={!email || !password || loading}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Signing in...
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <div className="text-center mt-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 active:text-slate-600 transition-colors"
        >
          <ArrowLeftIcon className="w-3.5 h-3.5" />
          Use email code instead
        </button>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP: Set Password (optional, after first sign-up)
// ═════════════════════════════════════════════════════════════════════

function SetPasswordStep({ onSubmit, onSkip, loading, error }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => passwordRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  const isValid = password.length >= 8 && password === confirm;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid) onSubmit(password);
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-[1.25rem] flex items-center justify-center mx-auto mb-4 border border-teal-100/60">
          <LockIcon className="w-8 h-8 text-teal-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Set a password?
        </h1>
        <p className="text-slate-500 leading-relaxed">
          Skip the email code next time.{'\n'}
          You can always use a code instead.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <label htmlFor="set-password" className="block text-sm font-medium text-slate-700 mb-2">Password</label>
          <input
            ref={passwordRef}
            id="set-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className="w-full px-5 py-4 pr-12 text-lg rounded-2xl border-2 border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-300"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-[42px] text-slate-400 active:text-slate-600"
          >
            {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
          </button>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-2">Confirm password</label>
          <input
            id="confirm-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Type it again"
            className="w-full px-5 py-4 text-lg rounded-2xl border-2 border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-300"
          />
        </div>

        {password && confirm && password !== confirm && (
          <p className="text-sm text-red-500 text-center">Passwords don't match</p>
        )}
        {password && password.length > 0 && password.length < 8 && (
          <p className="text-sm text-slate-400 text-center">At least 8 characters</p>
        )}

        {error && (
          <p className="text-sm text-red-500 text-center">{error}</p>
        )}

        <button
          type="submit"
          disabled={!isValid || loading}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {loading ? 'Setting password...' : 'Set Password'}
        </button>
      </form>

      <div className="text-center mt-4">
        <button
          onClick={onSkip}
          className="text-sm text-slate-400 font-medium active:text-slate-600 transition-colors"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP 2: Verification Code
// ═════════════════════════════════════════════════════════════════════

function CodeStep({ phone, email, onSubmit, onBack, onResend, onPasswordLogin, hasPassword, loading, error }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [resendCooldown, setResendCooldown] = useState(90);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    const timer = setTimeout(() => inputRefs.current[0]?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  // Clear code fields when an error occurs so user can re-enter
  useEffect(() => {
    if (error) {
      setCode(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [error]);

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (digit && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        onSubmit(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < pasted.length; i++) {
        newCode[i] = pasted[i];
      }
      setCode(newCode);
      const nextEmpty = Math.min(pasted.length, 5);
      inputRefs.current[nextEmpty]?.focus();
      if (pasted.length === 6) {
        onSubmit(pasted);
      }
    }
  };

  const handleResend = () => {
    if (resendCooldown > 0) return;
    setCode(['', '', '', '', '', '']);
    onResend();
    setResendCooldown(90);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Enter your code
        </h1>
        <p className="text-slate-500 leading-relaxed">
          We emailed a 6-digit code to<br />
          <span className="font-semibold text-slate-700">{email}</span>
        </p>
      </div>

      <div className="flex justify-center gap-2.5 mb-6" onPaste={handlePaste}>
        {code.map((digit, i) => (
          <input
            key={i}
            ref={el => inputRefs.current[i] = el}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digit}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all ${
              digit
                ? 'border-teal-400 bg-teal-50/50 text-slate-900'
                : 'border-slate-200 bg-white text-slate-900 focus:border-teal-400 focus:ring-4 ring-teal-500/10'
            }`}
          />
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 mb-4 text-teal-600">
          <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm font-medium">Verifying...</span>
        </div>
      )}

      {error && !loading && (
        <div className="text-center mb-4">
          <p className="text-sm text-red-600 font-medium">{error}</p>
        </div>
      )}

      <div className="text-center space-y-3">
        <button
          onClick={handleResend}
          disabled={resendCooldown > 0}
          className="text-sm font-medium text-teal-600 active:text-teal-700 disabled:text-slate-300 transition-colors"
        >
          {resendCooldown > 0
            ? `Resend code in ${resendCooldown}s`
            : 'Resend code'
          }
        </button>
        {hasPassword && onPasswordLogin && (
          <div>
            <button
              onClick={onPasswordLogin}
              className="text-sm text-teal-600 font-medium active:text-teal-700 transition-colors inline-flex items-center gap-1.5"
            >
              <LockIcon className="w-3.5 h-3.5" />
              Sign in with password instead
            </button>
          </div>
        )}
        <div>
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 active:text-slate-600 transition-colors"
          >
            <ArrowLeftIcon className="w-3.5 h-3.5" />
            Change email
          </button>
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP: Confirm new account (Hide My Email trap guard)
// ═════════════════════════════════════════════════════════════════════

function ConfirmNewAccountStep({ email, method, onContinue, onUseEmail }) {
  const providerLabel = method === 'google' ? 'Google account' : 'Apple ID';
  const isPrivateRelay = !!email && email.endsWith('@privaterelay.appleid.com');

  return (
    <div className="animate-fade-in text-center">
      <div className="w-16 h-16 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-[1.25rem] flex items-center justify-center mx-auto mb-4 border border-teal-100/60">
        <ShieldCheckIcon className="w-8 h-8 text-teal-600" />
      </div>

      <h1 className="text-2xl font-bold text-slate-900 mb-2">
        Create a new account?
      </h1>
      <p className="text-slate-500 leading-relaxed mb-3">
        This {providerLabel} isn't linked to an existing ClariMe account, so
        continuing will start a brand-new one with no visits in it.
      </p>
      {isPrivateRelay ? (
        <p className="text-sm text-slate-400 leading-relaxed mb-8">
          Your Apple ID is set to hide your email, so ClariMe can't see the
          address you may have used before. If you already have an account,
          sign in with your email instead.
        </p>
      ) : (
        <p className="text-sm text-slate-400 leading-relaxed mb-8">
          {email ? <>It signs in as <span className="font-semibold text-slate-500">{email}</span>. </> : null}
          If you already have an account under a different email, sign in with
          that email instead to find your visits.
        </p>
      )}

      <div className="space-y-3">
        <button
          onClick={onContinue}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all"
        >
          I'm new, create my account
        </button>
        <button
          onClick={onUseEmail}
          className="w-full py-3 text-teal-600 text-sm font-medium active:text-teal-700 transition-colors"
        >
          I already have an account
        </button>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP 3: Name (first-time only)
// ═════════════════════════════════════════════════════════════════════

function NameStep({ onSubmit, loading }) {
  const [firstName, setFirstName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(firstName.trim());
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          What should we call you?
        </h1>
        <p className="text-slate-500 leading-relaxed">
          This helps personalize your summaries.<br />
          You can skip this if you'd like.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-2">
            First name
          </label>
          <input
            ref={inputRef}
            id="firstName"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder="e.g. Margaret"
            className="w-full px-5 py-4 text-lg rounded-2xl border-2 border-slate-200 focus:border-teal-400 focus:ring-4 ring-teal-500/10 outline-none transition-all bg-white text-slate-900 placeholder:text-slate-300"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all disabled:opacity-40"
        >
          {loading ? 'Setting up...' : (firstName.trim() ? 'Continue' : 'Skip for now')}
        </button>
      </form>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP 4: Face ID Enrollment Prompt
// ═════════════════════════════════════════════════════════════════════

function FaceIdEnrollStep({ onEnable, onSkip, biometryLabel, error, loading }) {
  return (
    <div className="animate-fade-in text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-[1.5rem] flex items-center justify-center mx-auto mb-6 border border-teal-100/60">
        <FaceIdIcon className="w-10 h-10 text-teal-600" />
      </div>

      <h1 className="text-2xl font-bold text-slate-900 mb-2">
        Skip the code next time?
      </h1>
      <p className="text-slate-500 leading-relaxed mb-8">
        Use {biometryLabel} to open ClariMe instantly.{'\n'}
        No codes to type when you're at the doctor's office.
      </p>

      {error && (
        <p className="text-sm text-red-500 mb-4 px-2">{error}</p>
      )}

      <div className="space-y-3">
        <button
          onClick={onEnable}
          disabled={loading}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all disabled:opacity-40"
        >
          {loading ? 'Verifying…' : error ? `Try ${biometryLabel} Again` : `Enable ${biometryLabel}`}
        </button>
        <button
          onClick={onSkip}
          disabled={loading}
          className="w-full py-3 text-slate-400 text-sm font-medium active:text-slate-600 transition-colors disabled:opacity-40"
        >
          Not now
        </button>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// STEP 5: Success / Welcome
// ═════════════════════════════════════════════════════════════════════

function SuccessStep({ firstName, isReturning }) {
  useEffect(() => {
    const destination = (isReturning || hasCompletedOnboarding()) ? '/app' : '/setup';
    const timer = setTimeout(() => navigateTo(destination), 1800);
    return () => clearTimeout(timer);
  }, [isReturning]);

  return (
    <div className="animate-fade-in text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircleIcon className="w-10 h-10 text-teal-600" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">
        {firstName ? `Welcome, ${firstName}!` : "You're all set!"}
      </h1>
      <p className="text-slate-500 leading-relaxed mb-6">
        Your account is ready. Let's get started.
      </p>
      <div className="flex items-center justify-center gap-2 text-teal-600">
        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm font-medium">Loading your dashboard...</span>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// MAIN AUTH FLOW
// ═════════════════════════════════════════════════════════════════════

/**
 * Steps:
 *   biometric   — returning user with Face ID enrolled → auto-authenticate
 *   welcome     — first-time splash with logo, tagline, 3 icons, CTA
 *   phone       — phone number entry
 *   code        — 6-digit OTP verification
 *   name        — first name (new users only)
 *   faceid      — Face ID enrollment prompt (if device supports it)
 *   success     — checkmark + redirect
 */
// inviteContext (optional): { email, name, groupName, inviterName, status }
// loaded from a one-tap invite link (#/join/<token>). Pre-fills the invited
// email and shows who's inviting them; the token itself is redeemed by
// main.jsx once sign-in completes.
export default function AuthFlow({ auth, inviteContext = null }) {
  const [step, setStep] = useState('init');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(''); // reused as temp idToken storage
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [biometryLabel, setBiometryLabel] = useState('Face ID');
  const [authError, setAuthError] = useState(null);
  const [userHasPassword, setUserHasPassword] = useState(false);
  const [sessionNotice, setSessionNotice] = useState(null);
  // How the current sign-in happened: 'email' | 'apple' | 'google'. Gates the
  // set-password offer — Apple HIG Guideline 4 forbids prompting for a
  // password after Sign in with Apple (the Session 68 rejection), and the
  // same rule is applied to Google. Checked on EVERY path that can reach the
  // setPassword step, not just completeSignIn's direct route.
  const [signInMethod, setSignInMethod] = useState('email');
  // Stashed sign-in payload while the "Create a new account?" guard is shown
  // (social sign-in that just created a brand-new account — likely Hide My
  // Email, or a different address than this device has used before).
  const [pendingNewAccount, setPendingNewAccount] = useState(null);

  // Prevent iOS keyboard from pushing page behind Dynamic Island
  useIOSKeyboardFix();
  const keyboardHeight = useKeyboardHeight();

  // Initialize social login plugin on mount
  useEffect(() => {
    initSocialLogin().catch(() => {});
  }, []);

  // On mount: determine which step to show first
  useEffect(() => {
    let resolved = false;

    async function init() {
      try {
        const bio = await checkBiometricAvailability();
        if (resolved) return;

        const returning = hasExistingAccount();
        const enrolled = isBiometricEnrolled();

        // If main.jsx already fired a biometric prompt this session (and it
        // failed/cancelled — otherwise AuthFlow wouldn't be rendering), don't
        // re-fire it here. Consume the flag so that a later sign-out → remount
        // can still offer biometric on that fresh sign-in.
        const initialBiometricAlreadyAttempted =
          sessionStorage.getItem('clarime_initial_biometric_attempted') === '1';
        if (initialBiometricAlreadyAttempted) {
          sessionStorage.removeItem('clarime_initial_biometric_attempted');
        }

        // The user just tapped Sign Out. Auto-firing Face ID here would sign
        // them straight back in, making Sign Out look broken. Land on email;
        // biometric is back on the next cold launch.
        const explicitSignOut = sessionStorage.getItem('clarime_explicit_signout') === '1';
        if (explicitSignOut) {
          sessionStorage.removeItem('clarime_explicit_signout');
        }

        // The stored token is expired (set by main.jsx, useAuth's 401
        // listener, or the biometric step). Face ID would replay the same
        // dead token, so go straight to email with an explanation.
        const sessionExpired = sessionStorage.getItem('clarime_session_expired') === '1';
        if (sessionExpired) {
          sessionStorage.removeItem('clarime_session_expired');
          setSessionNotice("You've been signed out for your security. Enter your email to sign back in.");
        }

        if (bio.biometryType === 'touchId') setBiometryLabel('Touch ID');
        else if (bio.biometryType === 'faceId') setBiometryLabel('Face ID');
        else setBiometryLabel('Biometrics');

        setIsReturning(returning);

        if (returning && enrolled && bio.isAvailable && !initialBiometricAlreadyAttempted && !explicitSignOut && !sessionExpired) {
          setStep('biometric');
        } else if (returning) {
          setStep('email');
        } else {
          setStep('welcome');
        }
      } catch (err) {
        console.warn('[AuthFlow] init error, falling back to welcome:', err);
        if (!resolved) setStep('welcome');
      } finally {
        resolved = true;
      }
    }

    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        setStep('welcome');
      }
    }, 3000);

    init();
    return () => clearTimeout(timeout);
  }, []);

  // ── Shared post-auth logic ────────────────────────────────────────

  /**
   * After successful authentication (OTP, password, or social),
   * handle the next step: name collection, Face ID, password setup, or success.
   *
   * authMethod gates the password-setup prompt. Apple's HIG (Guideline 4)
   * forbids prompting for a password after Sign in with Apple — the
   * Authentication Services framework owns auth, passwords are not needed.
   * Google OAuth follows the same model. Only email-OTP users see the
   * optional password setup step.
   */
  const completeSignIn = async ({ idToken, emailAddr, name, isNew, hasPassword, authMethod = 'email', confirmedNewAccount = false }) => {
    setEmail(emailAddr);
    setPhone(idToken); // temp store for later steps
    setSignInMethod(authMethod);

    // Hide My Email trap guard: a social sign-in that just CREATED an account
    // is suspicious when (a) Apple handed us a private-relay address (the
    // user's real email is hidden, so it can't match their existing ClariMe
    // account), or (b) this device has signed in before under a different
    // email. Without this check, an existing patient who taps "Sign in with
    // Apple" + Hide My Email lands in a brand-new empty account and thinks
    // their visits are gone.
    if (!confirmedNewAccount && isNew && authMethod !== 'email') {
      let priorEmails = [];
      try { priorEmails = JSON.parse(localStorage.getItem('clarime_auth_history') || '[]'); } catch { /* ignore */ }
      const isPrivateRelay = !!emailAddr && emailAddr.endsWith('@privaterelay.appleid.com');
      const hasDifferentPriorAccount = priorEmails.length > 0 && !priorEmails.includes(emailAddr);
      if (isPrivateRelay || hasDifferentPriorAccount) {
        setPendingNewAccount({ idToken, emailAddr, name, isNew, hasPassword, authMethod });
        setStep('confirmNewAccount');
        return;
      }
    }

    if (isNew && !name) {
      // New user without a name — collect it
      setStep('name');
      return;
    }

    // name may come from the social plugin (first sign-up) OR the backend
    // response's firstName field (returning users — the plugin stops providing
    // name after first sign-in). Callers pass a fallback chain.
    if (name) setFirstName(name);
    recordAccountHistory(emailAddr);

    // Record password status so downstream steps (faceid → setPassword branching)
    // know whether to skip the setPassword screen.
    const alreadyHasPassword = hasPassword ?? userHasPassword;
    setUserHasPassword(alreadyHasPassword);

    // Check if we should offer Face ID enrollment
    const bio = await checkBiometricAvailability();
    if (bio.isAvailable && !isBiometricEnrolled()) {
      setStep('faceid');
      return;
    }

    // Already enrolled: silently rewrite the Keychain with this fresh JWT.
    // Without this, Face ID keeps replaying whatever token it was enrolled
    // with — once that expires, every cold launch is a broken session even
    // though the user keeps re-authenticating by email.
    updateBiometricToken(emailAddr, name || firstName, idToken);

    // Sign in before deciding where to go next
    auth?.signIn({ email: emailAddr, firstName: name || '', idToken });

    // Offer password setup ONLY to email-OTP users who don't have a password yet.
    // Social sign-ins (Apple, Google) skip this step per Apple HIG Guideline 4.
    if (authMethod === 'email' && !alreadyHasPassword) {
      setStep('setPassword');
      return;
    }

    setStep('success');
  };

  // ── Biometric ─────────────────────────────────────────────────────

  const handleBiometricSuccess = useCallback((bioEmail, bioFirstName, bioToken) => {
    recordAccountHistory(bioEmail);
    auth?.signIn({ email: bioEmail, firstName: bioFirstName, idToken: bioToken });
    setFirstName(bioFirstName);
    setIsReturning(true);
    setStep('success');
  }, [auth]);

  // ── Email OTP ─────────────────────────────────────────────────────

  const handleEmailSubmit = async (emailInput) => {
    setEmail(emailInput);
    setAuthError(null);
    setLoading(true);

    // Demo mode
    if (emailInput === DEMO_EMAIL) {
      setTimeout(() => {
        setLoading(false);
        setStep('code');
      }, 600);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/email/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send code');
      }

      // Track if this user has a password set so the code step can offer
      // a "Sign in with password instead" off-ramp.
      if (data.hasPassword) setUserHasPassword(true);

      setLoading(false);
      // Honor the button label ("Send Verification Code") — always show the
      // code step. Users with a password can switch to password login from
      // the code step or from the email step's "Sign in with password" link.
      setStep('code');
    } catch (err) {
      setLoading(false);
      setAuthError(err.message);
    }
  };

  const handleCodeSubmit = async (code) => {
    setLoading(true);
    setAuthError(null);

    // Demo mode
    if (email === DEMO_EMAIL) {
      if (code !== DEMO_CODE) {
        setLoading(false);
        return;
      }
      setTimeout(() => {
        setLoading(false);
        recordAccountHistory(DEMO_USER.email);
        auth?.signIn(DEMO_USER);
        setFirstName(DEMO_USER.firstName);
        setStep('success');
      }, 800);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/email/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Invalid code');
      }

      setLoading(false);
      await completeSignIn({
        idToken: data.idToken,
        emailAddr: email,
        name: data.firstName || null,
        isNew: data.isNewUser,
        hasPassword: data.hasPassword,
      });
    } catch (err) {
      setLoading(false);
      setAuthError(err.message);
    }
  };

  const handleResendCode = () => {
    if (email) handleEmailSubmit(email);
  };

  // ── Password login ────────────────────────────────────────────────

  const handlePasswordLogin = async (emailInput, password) => {
    setLoading(true);
    setAuthError(null);

    try {
      const data = await loginWithPassword(emailInput, password);
      setLoading(false);

      recordAccountHistory(emailInput);
      const name = data.firstName || '';
      setFirstName(name);
      setUserHasPassword(true); // trivially true — they just logged in with one
      auth?.signIn({ email: emailInput, firstName: name, idToken: data.idToken });
      setIsReturning(true);
      setStep('success');
    } catch (err) {
      setLoading(false);
      // Account locked after 3 failed attempts — backend returns HTTP 423.
      // Send a fresh OTP and route the user to the code step so they can unlock.
      if (err.status === 423) {
        setEmail(emailInput);
        try {
          await fetch(`${API_URL}/auth/email/request-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: emailInput }),
          });
        } catch {
          // Non-fatal — CodeStep has a Resend button if the code didn't arrive.
        }
        setAuthError(err.message);
        setStep('code');
        return;
      }
      setAuthError(err.message);
    }
  };

  // ── Set password (new user, optional) ─────────────────────────────

  const handleSetPassword = async (password) => {
    setLoading(true);
    setAuthError(null);

    try {
      await apiSetPassword(password);
      setLoading(false);
      setStep('success');
    } catch (err) {
      setLoading(false);
      setAuthError(err.message);
    }
  };

  const handleSkipSetPassword = () => {
    setStep('success');
  };

  // ── Social login ──────────────────────────────────────────────────

  const handleAppleSignIn = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      const appleResult = await signInWithApple();
      const data = await socialAppleSignIn(appleResult);
      setLoading(false);

      await completeSignIn({
        idToken: data.idToken,
        emailAddr: data.email,
        // Apple only returns fullName on first sign-up — fall back to the
        // backend-stored name for returning users so we don't lose it.
        name: appleResult.fullName || data.firstName || null,
        isNew: data.isNewUser,
        hasPassword: data.hasPassword,
        authMethod: 'apple',
      });
    } catch (err) {
      setLoading(false);
      // Don't show error if user cancelled (error 1001 = ASAuthorizationError.canceled)
      const msg = err.message || '';
      if (msg.includes('cancel') || msg.includes('Cancel') || msg.includes('1001')) return;
      setAuthError(msg);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setAuthError(null);

    try {
      const googleResult = await signInWithGoogle();
      const data = await socialGoogleSignIn(googleResult);
      setLoading(false);

      await completeSignIn({
        idToken: data.idToken,
        emailAddr: data.email,
        // Google may omit name on subsequent sign-ins — fall back to backend
        name: googleResult.name || data.firstName || null,
        isNew: data.isNewUser,
        hasPassword: data.hasPassword,
        authMethod: 'google',
      });
    } catch (err) {
      setLoading(false);
      if (err.message?.includes('cancel') || err.message?.includes('Cancel')) return;
      setAuthError(err.message);
    }
  };

  // ── New-account confirmation (Hide My Email trap guard) ──────────

  const handleConfirmNewAccount = () => {
    const pending = pendingNewAccount;
    setPendingNewAccount(null);
    if (pending) completeSignIn({ ...pending, confirmedNewAccount: true });
  };

  const handleDeclineNewAccount = () => {
    // The empty account already exists server-side; harmless to leave (no
    // data, and signing in with it again shows this same guard). Route the
    // user to email sign-in to find their real account.
    setPendingNewAccount(null);
    setAuthError(null);
    setIsReturning(true);
    setStep('email');
  };

  // ── Name submitted ────────────────────────────────────────────────

  const handleNameSubmit = async (name) => {
    setFirstName(name);
    setLoading(true);
    recordAccountHistory(email);

    // Persist the name server-side. Family share emails and group invites
    // read users.name from the DB — without this PATCH they all fall back to
    // "A family member shared a visit summary" instead of using the name.
    // Non-fatal on failure: the local copy still personalizes the app.
    try {
      await fetch(`${API_URL}/users/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${phone}` },
        body: JSON.stringify({ name }),
      });
    } catch (err) {
      console.warn('[Auth] Could not save name to profile:', err?.message);
    }

    const bio = await checkBiometricAvailability();
    setLoading(false);

    if (bio.isAvailable) {
      setStep('faceid');
    } else {
      auth?.signIn({ email, firstName: name, idToken: phone });
      // Password setup is for email-OTP users only (Guideline 4, Session 68).
      if (signInMethod === 'email' && !userHasPassword) {
        setStep('setPassword');
      } else {
        setStep('success');
      }
    }
  };

  // ── Face ID ───────────────────────────────────────────────────────

  const handleEnableFaceId = async () => {
    // Sign in FIRST so auth_token is in localStorage before enrollBiometric reads it.
    // Otherwise the Keychain stores an empty JWT and biometric auto-login on reopen
    // restores a broken session.
    auth?.signIn({ email, firstName, idToken: phone });

    setLoading(true);
    setAuthError(null);
    // Pass the JWT explicitly — don't rely on localStorage timing. This
    // guarantees the Keychain entry will always have a real token.
    const result = await enrollBiometric(email, firstName, phone);
    setLoading(false);

    if (!result?.ok) {
      // Log the full structured result so device logs show the exact stage + code
      console.warn('[Auth] Biometric enrollment failed:', JSON.stringify(result));

      // Silent cancel — user tapped Cancel on the system prompt. Don't badger them.
      if (result?.stage === 'verify' && result?.code === 16) {
        return;
      }

      // Show the specific error from the plugin (with code) so we can diagnose
      // on-device. Falls back to the generic message if we didn't get detail.
      const msg = result?.userMessage || `Couldn't enable ${biometryLabel}. Try again or tap Not now.`;
      setAuthError(msg);
      return;
    }

    console.log('[Auth] Biometric enrolled successfully');

    // Offer password setup ONLY to email-OTP users without one. Apple HIG
    // Guideline 4 forbids prompting for a password after Sign in with Apple
    // (the Session 68 rejection) — and Face ID devices reach this exit
    // instead of completeSignIn's gate, so the same check is required here.
    if (signInMethod === 'email' && !userHasPassword) {
      setStep('setPassword');
    } else {
      setStep('success');
    }
  };

  const handleSkipFaceId = () => {
    auth?.signIn({ email, firstName, idToken: phone });
    setAuthError(null); // clear any "Couldn't enable Face ID" message
    // Same Guideline 4 gate as handleEnableFaceId.
    if (signInMethod === 'email' && !userHasPassword) {
      setStep('setPassword');
    } else {
      setStep('success');
    }
  };

  // ── Back to email ─────────────────────────────────────────────────

  const handleBackToEmail = () => {
    setStep('phone');
    setEmail('');
    setAuthError(null);
  };

  // ── Render ──────────────────────────────────────────────────────


  if (step === 'init') {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <EmbraceMark className="w-14 h-14 animate-pulse" id="init-mark" />
      </div>
    );
  }

  if (step === 'biometric') {
    return (
      <div className="fixed inset-0 bg-white" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <BiometricLoginStep
          onSuccess={handleBiometricSuccess}
          onFallback={() => {
            // If the fallback was triggered by an expired Keychain token,
            // surface the notice on the email step.
            if (sessionStorage.getItem('clarime_session_expired') === '1') {
              sessionStorage.removeItem('clarime_session_expired');
              setSessionNotice("You've been signed out for your security. Enter your email to sign back in.");
            }
            setStep('phone');
          }}
          biometryLabel={biometryLabel}
        />
      </div>
    );
  }

  if (step === 'welcome') {
    return (
      <div className="fixed inset-0 bg-white" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <WelcomeIntroStep
          onGetStarted={() => setStep('email')}
          onSignIn={() => {
            setIsReturning(true);
            setStep('email');
          }}
        />
      </div>
    );
  }

  // Steps with header
  const showBack = step === 'code' || step === 'name' || step === 'password';
  const stepOrder = isReturning
    ? ['email', 'code']
    : ['email', 'code', 'name'];
  const displayStep = step === 'phone' ? 'email' : step;
  const stepIndex = stepOrder.indexOf(displayStep);

  return (
    <div
      className="fixed inset-x-0 top-0 bg-white flex flex-col overflow-hidden"
      style={{
        height: keyboardHeight > 0 ? `calc(100% - ${keyboardHeight}px)` : '100dvh',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: keyboardHeight > 0 ? 0 : 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Header */}
      <div className="pt-4 pb-3 px-5 flex items-center justify-between shrink-0 z-10 bg-white">
        <div className="flex items-center gap-2 min-w-0">
          {showBack && (
            <button
              onClick={
                step === 'code' ? handleBackToEmail
                : step === 'password' ? () => { setStep('email'); setAuthError(null); }
                : () => setStep('code')
              }
              className="w-9 h-9 rounded-xl flex items-center justify-center active:bg-slate-100 transition-colors -ml-1"
            >
              <ArrowLeftIcon className="w-5 h-5 text-slate-400" />
            </button>
          )}
          <ClariMeLogo size="small" variant="light" />
        </div>

        {stepIndex >= 0 && step !== 'faceid' && step !== 'success' && step !== 'setPassword' && step !== 'password' && (
          <div className="flex items-center gap-2">
            {stepOrder.map((s, i) => (
              <div
                key={s}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  s === displayStep
                    ? 'w-6 bg-gradient-to-r from-teal-500 to-cyan-500'
                    : i < stepIndex
                    ? 'w-3 bg-teal-300'
                    : 'w-3 bg-slate-200'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-6 pt-6 overflow-y-auto">
        <div className="w-full max-w-sm">
          {(step === 'phone' || step === 'email') && (
            <EmailStep
              onSubmit={handleEmailSubmit}
              loading={loading}
              isReturning={isReturning}
              error={authError}
              notice={sessionNotice || (inviteContext
                ? `${inviteContext.inviterName} added you to "${inviteContext.groupName}". Sign in below and you'll be connected automatically.`
                : null)}
              initialEmail={inviteContext?.email || ''}
              onAppleSignIn={handleAppleSignIn}
              onGoogleSignIn={handleGoogleSignIn}
              onPasswordLogin={() => { setAuthError(null); setStep('password'); }}
              hasPassword={userHasPassword}
            />
          )}
          {step === 'password' && (
            <PasswordStep
              email={email}
              onSubmit={handlePasswordLogin}
              onBack={() => { setAuthError(null); setStep('email'); }}
              loading={loading}
              error={authError}
            />
          )}
          {step === 'code' && (
            <CodeStep
              email={email}
              onSubmit={handleCodeSubmit}
              onBack={handleBackToEmail}
              onResend={handleResendCode}
              onPasswordLogin={() => { setAuthError(null); setStep('password'); }}
              hasPassword={userHasPassword}
              loading={loading}
              error={authError}
            />
          )}
          {step === 'name' && (
            <NameStep onSubmit={handleNameSubmit} loading={loading} />
          )}
          {step === 'confirmNewAccount' && (
            <ConfirmNewAccountStep
              email={pendingNewAccount?.emailAddr}
              method={pendingNewAccount?.authMethod}
              onContinue={handleConfirmNewAccount}
              onUseEmail={handleDeclineNewAccount}
            />
          )}
          {step === 'faceid' && (
            <FaceIdEnrollStep
              onEnable={handleEnableFaceId}
              onSkip={handleSkipFaceId}
              biometryLabel={biometryLabel}
              error={authError}
              loading={loading}
            />
          )}
          {step === 'setPassword' && (
            <SetPasswordStep
              onSubmit={handleSetPassword}
              onSkip={handleSkipSetPassword}
              loading={loading}
              error={authError}
            />
          )}
          {step === 'success' && (
            <SuccessStep firstName={firstName} isReturning={isReturning} />
          )}
        </div>
      </div>
    </div>
  );
}
